import { Fragment, KeyboardEvent, ReactNode, createContext, useContext } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import { METRIC_COLUMNS, fmtQty } from "./pivotFormat";
import { PV } from "./pivotTokens";
import { GroupRef, Metrics } from "../../types/pivot.type";

export interface PivotTableGroup<TRow> {
  group: GroupRef;
  is_flat?: boolean;
  row_count: number;
  truncated_row_count: number;
  rows: TRow[];
  totals: Metrics;
}

const HEAD_CELL = {
  bgcolor: PV.head,
  color: PV.tx2,
  fontWeight: 500,
  fontSize: 13,
  padding: "12px 16px",
  whiteSpace: "nowrap",
  borderBottom: `1px solid ${PV.line}`,
  position: "sticky",
  top: 0,
  zIndex: 2,
} as const;

const BODY_CELL = {
  padding: "13px 16px",
  borderBottom: `1px solid ${PV.line2}`,
  verticalAlign: "middle",
  fontSize: 13.5,
} as const;

export function SortableHeaderCell({
  label,
  align,
  sortKey,
  sortBy,
  sortDir,
  onSort,
  sx,
  rowSpan,
}: {
  label: string;
  align: "left" | "right" | "center";
  sortKey: string;
  sortBy: string;
  sortDir: "asc" | "desc";
  onSort: (k: string) => void;
  sx?: object;
  rowSpan?: number;
}) {
  const active = sortBy === sortKey;
  return (
    <Box
      component="th"
      rowSpan={rowSpan}
      onClick={() => onSort(sortKey)}
      sx={{
        ...HEAD_CELL,
        textAlign: align,
        cursor: "pointer",
        userSelect: "none",
        color: active ? PV.indigo : PV.tx2,
        fontWeight: active ? 600 : 500,
        "&:hover": { color: PV.indigo },
        ...sx,
      }}
    >
      {label}
      {active && (
        <Box component="span" sx={{ color: PV.indigo, ml: 0.5, fontSize: 10 }}>
          {sortDir === "desc" ? "▾" : "▴"}
        </Box>
      )}
    </Box>
  );
}

/** A read-only column appended after the metrics (not sortable — the API cannot sort by it). */
export interface PivotExtraColumn<TRow> {
  key: string;
  label: string;
  align: "left" | "right";
  render: (row: TRow) => ReactNode;
}

/**
 * A band of extra columns under one shared heading, visually set apart from the metrics
 * (tinted, left rule) — the สายส่ง tab's "เวลา (PWA)" block.
 */
export interface PivotExtraColumnGroup<TRow> {
  title: ReactNode;
  columns: PivotExtraColumn<TRow>[];
  /** Content for the band on a group heading row, e.g. how many stores have data. */
  renderGroup?: (group: PivotTableGroup<TRow>) => ReactNode;
}

/** An identity column heading; with a `sortKey` it sorts like the metric columns do. */
export type PivotIdentityHeader = string | { label: string; sortKey: string };

/**
 * How many identity columns the enclosing table has, so drill rows (`PivotDetailRow`,
 * `PivotNoteRow`) line up with it without every caller passing the count down.
 */
const IdentityColsContext = createContext(2);

const EXTRA_BAND_BORDER = `2px solid ${PV.indigoBorder}`;
/** Height of the band's title row; the sub-header row sticks just under it. */
const EXTRA_TITLE_ROW_PX = 33;

interface PivotGroupedTableProps<TRow> {
  groups: PivotTableGroup<TRow>[];
  grandTotal: Metrics;
  totalRowCount: number;
  groupLabel: string;
  unit: string;
  /** One entry per identity column (the product tab has 3, store tabs 2); the first carries the caret. */
  identityHeader: PivotIdentityHeader[];
  renderIdentity: (row: TRow) => ReactNode[];
  getRowKey: (row: TRow) => string;
  sortBy: string;
  sortDir: "asc" | "desc";
  onSort: (key: string) => void;
  collapsed: Set<string>;
  onToggleGroup: (key: string) => void;
  loading?: boolean;
  /** Stores inside the filters with no visit at all — reported, never silently dropped. */
  skippedRowCount?: number;
  /**
   * Makes every body row a drill target: pointer cursor, focusable, Enter/Space opens it.
   * The row keeps its hover tint either way; only clickable rows get the indigo one.
   */
  onRowClick?: (row: TRow, group: GroupRef) => void;
  /** Accessible name for a clickable row (read out on focus). */
  getRowActionLabel?: (row: TRow) => string;
  /**
   * Inline drill: when given, a caret leads the first cell and whatever this returns (table
   * rows built from `PivotDetailRow` / `PivotNoteRow`) is rendered right under the row.
   */
  isRowExpanded?: (row: TRow, group: GroupRef) => boolean;
  renderRowDetail?: (row: TRow, group: GroupRef, isFlat: boolean) => ReactNode;
  extraColumns?: PivotExtraColumnGroup<TRow>;
}

const Caret = ({ open }: { open: boolean }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      display: "inline-block",
      width: 15,
      color: PV.tx3,
      fontSize: 10,
      transition: "transform .12s",
      transform: open ? "none" : "rotate(-90deg)",
    }}
  >
    ▾
  </Box>
);

/**
 * A nested row inside an expanded row: same columns as the table, indented identity, and
 * the standard metric cells. `metrics = null` renders a skeleton row. When the table has
 * more identity columns than `identity` has entries, the first cell spans the extra ones.
 */
export function PivotDetailRow({
  indent,
  identity,
  metrics,
  strong,
  caret,
  onClick,
  actionLabel,
}: {
  /** Left padding of the first cell in px. */
  indent: number;
  identity: [ReactNode, ReactNode];
  metrics: Metrics | null;
  strong?: boolean;
  /** `undefined` = no caret; otherwise open/closed. */
  caret?: boolean;
  onClick?: () => void;
  actionLabel?: string;
}) {
  const cell = {
    ...BODY_CELL,
    padding: "9px 16px",
    fontSize: strong ? 13 : 12.5,
    fontWeight: strong ? 600 : 400,
    bgcolor: strong ? PV.rowHover : PV.card,
  };
  const leadSpan = useContext(IdentityColsContext) - identity.length + 1;
  return (
    <Box
      component="tr"
      {...(onClick
        ? {
            tabIndex: 0,
            role: "button",
            "aria-label": actionLabel,
            "aria-expanded": caret,
            onClick,
            onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            },
          }
        : {})}
      sx={{
        cursor: onClick ? "pointer" : undefined,
        outline: "none",
        "&:hover > td": onClick ? { bgcolor: PV.indigoTint } : undefined,
        "&:focus-visible > td": { bgcolor: PV.indigoTint },
      }}
    >
      <Box
        component="td"
        colSpan={leadSpan > 1 ? leadSpan : undefined}
        sx={{ ...cell, pl: `${indent}px`, whiteSpace: "nowrap" }}
      >
        {caret !== undefined && <Caret open={caret} />}
        {metrics ? (
          identity[0]
        ) : (
          <Skeleton variant="text" width={70} sx={{ display: "inline-block" }} />
        )}
      </Box>
      <Box component="td" sx={cell}>
        {metrics ? identity[1] : <Skeleton variant="text" />}
      </Box>
      {METRIC_COLUMNS.map((c) => (
        <Box
          component="td"
          key={c.key}
          sx={{
            ...cell,
            textAlign: c.align,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {metrics ? c.render(metrics[c.key] as number | null) : <Skeleton variant="text" />}
        </Box>
      ))}
    </Box>
  );
}

/** A full-width note line inside an expanded row (truncation, caveats, actions). */
export function PivotNoteRow({ indent, children }: { indent: number; children: ReactNode }) {
  const identityCols = useContext(IdentityColsContext);
  return (
    <Box component="tr">
      <Box
        component="td"
        colSpan={identityCols + METRIC_COLUMNS.length}
        sx={{
          ...BODY_CELL,
          padding: "8px 16px",
          pl: `${indent}px`,
          fontSize: 12.5,
          color: PV.tx2,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default function PivotGroupedTable<TRow extends { metrics: Metrics }>({
  groups,
  grandTotal,
  totalRowCount,
  groupLabel,
  unit,
  identityHeader,
  renderIdentity,
  getRowKey,
  sortBy,
  sortDir,
  onSort,
  collapsed,
  onToggleGroup,
  loading,
  skippedRowCount,
  onRowClick,
  getRowActionLabel,
  isRowExpanded,
  renderRowDetail,
  extraColumns,
}: PivotGroupedTableProps<TRow>) {
  const identityCols = identityHeader.length;
  const extras = extraColumns?.columns ?? [];
  const columnCount = identityCols + METRIC_COLUMNS.length + extras.length;
  const headRowSpan = extras.length ? 2 : undefined;
  const extraCellSx = (i: number) => ({
    bgcolor: PV.rowHover,
    borderLeft: i === 0 ? EXTRA_BAND_BORDER : undefined,
  });
  const isFlat = groups.some((g) => g.is_flat);

  return (
    <IdentityColsContext.Provider value={identityCols}>
      <Box sx={{ overflowX: "auto", maxHeight: 640, overflowY: "auto" }}>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
          <Box component="thead">
            <Box component="tr">
              {identityHeader.map((h, i) => {
                // The first heading clears the caret column that leads every body row.
                const lead = i === 0 ? { pl: "31px" } : undefined;
                return typeof h === "string" ? (
                  <Box
                    key={h}
                    component="th"
                    rowSpan={headRowSpan}
                    sx={{ ...HEAD_CELL, textAlign: "left", ...lead }}
                  >
                    {h}
                  </Box>
                ) : (
                  <SortableHeaderCell
                    key={h.sortKey}
                    label={h.label}
                    align="left"
                    sortKey={h.sortKey}
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={onSort}
                    rowSpan={headRowSpan}
                    sx={lead}
                  />
                );
              })}
              {METRIC_COLUMNS.map((c) => (
                <SortableHeaderCell
                  key={c.key}
                  label={c.label}
                  align={c.align}
                  sortKey={c.key}
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                  rowSpan={headRowSpan}
                />
              ))}
              {extraColumns && extras.length > 0 && (
                <Box
                  component="th"
                  colSpan={extras.length}
                  sx={{
                    ...HEAD_CELL,
                    height: EXTRA_TITLE_ROW_PX,
                    boxSizing: "border-box",
                    padding: "6px 12px",
                    textAlign: "left",
                    bgcolor: PV.indigoTint,
                    color: PV.indigoDark,
                    fontWeight: 700,
                    borderLeft: EXTRA_BAND_BORDER,
                    borderBottom: 0,
                  }}
                >
                  {extraColumns.title}
                </Box>
              )}
            </Box>
            {extras.length > 0 && (
              <Box component="tr">
                {extras.map((c, i) => (
                  <Box
                    component="th"
                    key={c.key}
                    sx={{
                      ...HEAD_CELL,
                      top: EXTRA_TITLE_ROW_PX,
                      padding: "6px 12px 10px",
                      textAlign: c.align,
                      bgcolor: PV.indigoTint,
                      borderLeft: i === 0 ? EXTRA_BAND_BORDER : undefined,
                    }}
                  >
                    {c.label}
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Box component="tbody">
            {loading &&
              Array.from({ length: 6 }).map((_, i) => (
                <Box component="tr" key={i}>
                  {Array.from({ length: columnCount }).map((__, j) => (
                    <Box component="td" key={j} sx={BODY_CELL}>
                      <Skeleton variant="text" />
                    </Box>
                  ))}
                </Box>
              ))}

            {!loading && !groups.length && (
              <Box component="tr">
                <Box component="td" colSpan={columnCount} sx={{ ...BODY_CELL, borderBottom: 0 }}>
                  <Box sx={{ py: 5, textAlign: "center", color: PV.tx3 }}>
                    <Typography sx={{ fontWeight: 600, color: PV.tx2, mb: 0.5 }}>
                      ไม่มีข้อมูลตามตัวกรอง
                    </Typography>
                    <Typography sx={{ fontSize: 13 }}>
                      ลองขยายช่วงวันที่ ล้างคำค้น หรือเปลี่ยนขอบเขต
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {!loading &&
              groups.map((g) => {
                const isCollapsed = collapsed.has(g.group.key);
                return (
                  <Fragment key={g.group.key}>
                    {!g.is_flat && (
                      <Box
                        component="tr"
                        onClick={() => onToggleGroup(g.group.key)}
                        sx={{
                          cursor: "pointer",
                          "& > td": {
                            bgcolor: PV.head,
                            borderTop: `1px solid ${PV.line}`,
                            borderBottom: `1px solid ${PV.line2}`,
                            padding: "11px 16px",
                            whiteSpace: "nowrap",
                            fontWeight: 600,
                            fontVariantNumeric: "tabular-nums",
                          },
                          "&:hover > td": { bgcolor: "#EDEFF6" },
                        }}
                      >
                        <Box component="td" colSpan={identityCols}>
                          <Box
                            component="span"
                            sx={{
                              display: "inline-block",
                              width: 15,
                              color: PV.tx3,
                              fontSize: 10,
                              transition: "transform .12s",
                              transform: isCollapsed ? "rotate(-90deg)" : "none",
                            }}
                          >
                            ▾
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              fontSize: 13.5,
                              fontWeight: 600,
                              color: PV.tx,
                            }}
                          >
                            {g.group.label ?? g.group.key}
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              fontSize: 11.5,
                              color: PV.tx3,
                              ml: 1.1,
                              fontWeight: 400,
                            }}
                          >
                            {fmtQty(g.row_count)} {unit}
                          </Box>
                        </Box>
                        {METRIC_COLUMNS.map((c) => (
                          <Box component="td" key={c.key} sx={{ textAlign: c.align }}>
                            {c.render(g.totals[c.key] as number | null)}
                          </Box>
                        ))}
                        {extras.length > 0 && (
                          <Box
                            component="td"
                            colSpan={extras.length}
                            sx={{
                              borderLeft: EXTRA_BAND_BORDER,
                              color: PV.indigoDark,
                              fontSize: 12.5,
                            }}
                          >
                            {extraColumns?.renderGroup?.(g)}
                          </Box>
                        )}
                      </Box>
                    )}

                    {!isCollapsed &&
                      g.rows.map((row) => {
                        const [c1, ...rest] = renderIdentity(row);
                        const expanded = isRowExpanded?.(row, g.group);
                        return (
                          <Fragment key={getRowKey(row)}>
                            <Box
                              component="tr"
                              {...(onRowClick
                                ? {
                                    tabIndex: 0,
                                    role: "button",
                                    "aria-label": getRowActionLabel?.(row),
                                    "aria-expanded": expanded,
                                    onClick: () => onRowClick(row, g.group),
                                    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        onRowClick(row, g.group);
                                      }
                                    },
                                  }
                                : {})}
                              sx={
                                onRowClick
                                  ? {
                                      cursor: "pointer",
                                      outline: "none",
                                      "&:hover > td": {
                                        bgcolor: PV.indigoTint,
                                      },
                                      "&:focus-visible > td": {
                                        bgcolor: PV.indigoTint,
                                        boxShadow: `inset 0 1px 0 ${PV.indigoBorder}, inset 0 -1px 0 ${PV.indigoBorder}`,
                                      },
                                    }
                                  : { "&:hover > td": { bgcolor: PV.rowHover } }
                              }
                            >
                              <Box
                                component="td"
                                sx={{
                                  ...BODY_CELL,
                                  pl: expanded === undefined ? "31px" : "16px",
                                  whiteSpace: "nowrap",
                                  ...(expanded ? { bgcolor: PV.rowHover, fontWeight: 600 } : {}),
                                }}
                              >
                                {expanded !== undefined && <Caret open={expanded} />}
                                {c1}
                              </Box>
                              {rest.map((cell, i) => (
                                <Box component="td" key={i} sx={BODY_CELL}>
                                  {cell}
                                </Box>
                              ))}
                              {METRIC_COLUMNS.map((c) => (
                                <Box
                                  component="td"
                                  key={c.key}
                                  sx={{
                                    ...BODY_CELL,
                                    textAlign: c.align,
                                    fontVariantNumeric: "tabular-nums",
                                  }}
                                >
                                  {c.render(row.metrics[c.key] as number | null)}
                                </Box>
                              ))}
                              {extras.map((c, i) => (
                                <Box
                                  component="td"
                                  key={c.key}
                                  sx={{
                                    ...BODY_CELL,
                                    ...extraCellSx(i),
                                    padding: "13px 12px",
                                    textAlign: c.align,
                                    whiteSpace: "nowrap",
                                    fontVariantNumeric: "tabular-nums",
                                  }}
                                >
                                  {c.render(row)}
                                </Box>
                              ))}
                            </Box>
                            {expanded && renderRowDetail?.(row, g.group, !!g.is_flat)}
                          </Fragment>
                        );
                      })}

                    {!isCollapsed && g.truncated_row_count > 0 && (
                      <Box component="tr">
                        <Box
                          component="td"
                          colSpan={columnCount}
                          sx={{
                            ...BODY_CELL,
                            pl: "31px",
                            fontSize: 12.5,
                            color: PV.tx3,
                            fontStyle: "italic",
                          }}
                        >
                          …อีก {fmtQty(g.truncated_row_count)} {unit}
                          ในกลุ่มนี้ที่ไม่ได้แสดงบนจอ · ดาวน์โหลด CSV เพื่อดูครบ
                        </Box>
                      </Box>
                    )}
                  </Fragment>
                );
              })}
          </Box>

          {!loading && groups.length > 0 && (
            <Box component="tfoot">
              <Box
                component="tr"
                sx={{
                  "& > td": {
                    bgcolor: PV.head,
                    fontWeight: 600,
                    fontSize: 13,
                    borderTop: `1px solid ${PV.line}`,
                    padding: "13px 16px",
                    whiteSpace: "nowrap",
                    fontVariantNumeric: "tabular-nums",
                    position: "sticky",
                    bottom: 0,
                  },
                }}
              >
                <Box component="td" colSpan={identityCols} sx={{ pl: "31px" }}>
                  รวมทั้งหมด · {fmtQty(totalRowCount)} {unit}
                  {!isFlat && ` · ${fmtQty(groups.length)} ${groupLabel}`}
                </Box>
                {METRIC_COLUMNS.map((c) => (
                  <Box component="td" key={c.key} sx={{ textAlign: c.align }}>
                    {c.render(grandTotal[c.key] as number | null)}
                  </Box>
                ))}
                {extras.length > 0 && (
                  <Box component="td" colSpan={extras.length} sx={{ borderLeft: EXTRA_BAND_BORDER }} />
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {!loading && !!skippedRowCount && (
        <Box
          sx={{
            px: "31px",
            py: 1.5,
            fontSize: 12.5,
            color: PV.tx2,
            borderTop: `1px solid ${PV.line2}`,
            bgcolor: PV.rowHover,
          }}
        >
          อีก <b>{fmtQty(skippedRowCount)}</b> {unit}
          ในขอบเขตนี้ไม่มีรอบเข้าร้านเลยในช่วงที่เลือก จึงไม่ได้อยู่ในตาราง
        </Box>
      )}
    </IdentityColsContext.Provider>
  );
}
